import dayjs from 'dayjs';
import {CadenceFormState} from './types';
import {
  DAILY_CADENCE_DAYS,
  UI_SELECTED_DAYS_FREQUENCY,
} from './cadence-frequency';

const DAY_NAME_MAP: Record<string, string> = {
  sun: 'Sunday',
  sunday: 'Sunday',
  mon: 'Monday',
  monday: 'Monday',
  tue: 'Tuesday',
  tuesday: 'Tuesday',
  wed: 'Wednesday',
  wednesday: 'Wednesday',
  thu: 'Thursday',
  thursday: 'Thursday',
  fri: 'Friday',
  friday: 'Friday',
  sat: 'Saturday',
  saturday: 'Saturday',
};

const UI_ONE_TIME_FREQUENCY = 'one-time';
const API_ONE_TIME_FREQUENCY = 'once-for-workspace';

export const toFullDayName = (value?: string) => {
  if (!value) return '';
  return DAY_NAME_MAP[value.toLowerCase()] || value;
};

const to24HourTime = (time: string, period: string) => {
  if (!time) return '';

  const [hoursPart, minutesPart = '00'] = time.split(':');
  let hours = parseInt(hoursPart, 10);
  if (Number.isNaN(hours)) return '';

  const normalizedPeriod = period?.toLowerCase();
  if (normalizedPeriod === 'pm' && hours < 12) hours += 12;
  if (normalizedPeriod === 'am' && hours === 12) hours = 0;

  return `${String(hours).padStart(2, '0')}:${minutesPart}`;
};

const normalizeDate = (value?: string) => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.toISOString() : null;
};

const toApiFrequency = (frequency?: string) =>
  frequency === UI_ONE_TIME_FREQUENCY
    ? API_ONE_TIME_FREQUENCY
    : frequency === UI_SELECTED_DAYS_FREQUENCY
    ? 'daily'
    : frequency || 'weekly';

export const buildCadenceConfig = (cadence: CadenceFormState) => {
  const isOneTime = cadence.frequency === UI_ONE_TIME_FREQUENCY;
  const sendTime = to24HourTime(
    cadence.selectedTime.time,
    cadence.selectedTime.period,
  );
  const timezone = cadence.timezone === 'workspace' ? 'workspace' : 'user';

  const startDate = isOneTime
    ? cadence.startOption === 'specific'
      ? normalizeDate(cadence.oneTimeDate) || dayjs().toISOString()
      : dayjs().toISOString()
    : cadence.startOption === 'specific'
    ? normalizeDate(cadence.startDate) || dayjs().toISOString()
    : dayjs().toISOString();

  const endDate =
    !isOneTime && cadence.endOption === 'specific'
      ? normalizeDate(cadence.endDate)
      : undefined;

  const endsAfterOccurrences =
    !isOneTime && cadence.endOption === 'after'
      ? Number(cadence.occurrences) || undefined
      : undefined;

  const dayOfWeek = isOneTime
    ? undefined
    : cadence.frequency === 'daily'
    ? DAILY_CADENCE_DAYS.map(toFullDayName)
    : cadence.frequency === UI_SELECTED_DAYS_FREQUENCY
    ? cadence.selectedDays.length
      ? cadence.selectedDays.map(toFullDayName)
      : undefined
    : cadence.frequency === 'monthly'
    ? undefined
    : cadence.selectedDay
    ? [toFullDayName(cadence.selectedDay)]
    : undefined;

  const dayOfMonth =
    cadence.frequency === 'monthly' && cadence.monthlySpecificDay
      ? Number(cadence.monthlySpecificDay) || undefined
      : undefined;

  return {
    frequency: toApiFrequency(cadence.frequency),
    dayOfWeek,
    dayOfMonth,
    sendTime: sendTime || '09:00',
    timezone,
    startDate,
    endDate,
    endsAfterOccurrences,
  };
};
